import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarClock,
  CircleCheck,
  CircleAlert,
  Clock3,
  HandCoins,
  Search,
  ShieldCheck,
  X,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  getOpenQuickOrderPreview,
  grabQuickOrder,
  listOpenQuickOrders,
} from "../../entities/order/api";
import { getMyProviderOnboarding } from "../../entities/provider/api";
import { getApiErrorMessage } from "../../shared/api/errors";
import { showToast } from "../../shared/ui/toast";

function formatDate(value) {
  if (!value) return "刚刚";
  return new Date(value).toLocaleDateString("zh-CN");
}

function formatAmount(value) {
  return Number(value || 0).toLocaleString("zh-CN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function OrderPreview({ orderId, onClose, onGrab, grabbing }) {
  const previewQuery = useQuery({
    queryKey: ["orders", "open", "preview", orderId],
    queryFn: () => getOpenQuickOrderPreview(orderId),
    enabled: Boolean(orderId),
  });
  const order = previewQuery.data?.order;
  const materials = previewQuery.data?.materials || [];
  const dispatchLabel = order?.dispatch_type === "RESERVATION" ? "预约需求" : "即时需求";

  return (
    <div className="grab-preview-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="grab-preview-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="grab-preview-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button className="grab-preview-close" type="button" aria-label="关闭详情" onClick={onClose}>
          <X size={20} />
        </button>
        {previewQuery.isPending ? <div className="grab-preview-state">正在加载需求详情……</div> : null}
        {previewQuery.isError ? (
          <div className="grab-preview-state error">
            {getApiErrorMessage(previewQuery.error, "需求详情加载失败")}
          </div>
        ) : null}
        {previewQuery.isSuccess && order ? (
          <div className="grab-preview-content">
            <span className="eyebrow">需求详情</span>
            <h2 id="grab-preview-title">{order.title}</h2>
            <div className="grab-preview-meta">
              <strong>¥{formatAmount(order.amount_yuan)}</strong>
              <span>{dispatchLabel}</span>
              <span>发布于 {formatDate(order.created_at)}</span>
            </div>
            <dl className="grab-preview-details">
              <div><dt>需求说明</dt><dd>{order.requirement_desc || "暂无说明"}</dd></div>
              <div><dt>使用场景</dt><dd>{order.usage_scenario || "未填写"}</dd></div>
              <div><dt>交付物</dt><dd>{order.deliverable_desc || "按双方确认内容交付"}</dd></div>
              <div><dt>验收标准</dt><dd>{order.acceptance_criteria || "按平台标准验收"}</dd></div>
              <div><dt>期望初稿</dt><dd>{order.expected_draft_label || order.preferred_start_label || "待沟通"}</dd></div>
              <div><dt>修改轮次</dt><dd>{order.revision_rounds ?? 0} 轮</dd></div>
            </dl>
            {materials.length ? (
              <p className="grab-preview-materials"><ShieldCheck size={16} /> 已附带 {materials.length} 份参考素材</p>
            ) : null}
            <button
              className="primary-button grab-confirm-button"
              type="button"
              disabled={grabbing || order.can_grab === false}
              onClick={() => onGrab(order.id)}
            >
              {grabbing ? "正在抢单……" : "确认抢单"}
            </button>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function ProviderActivationNotice({ provider, isLoading, isError, error, onNavigate }) {
  if (isLoading) {
    return <div className="grab-list-state">正在校验服务者准入状态……</div>;
  }

  if (isError) {
    return (
      <button
        className="grab-list-state error actionable"
        type="button"
        onClick={onNavigate}
      >
        <CircleAlert size={29} />
        <strong>服务者状态校验失败</strong>
        <p>{getApiErrorMessage(error, "请前往服务者申请页确认准入状态。")}</p>
        <span>点击前往服务者申请</span>
      </button>
    );
  }

  const status = String(provider?.status || "NOT_APPLIED").toUpperCase();
  const message = status === "NOT_APPLIED"
    ? "当前账户尚未提交服务者申请，完成准入后才可接单。"
    : "当前账户还不是已激活服务者，完成准入流程后才可接单。";

  return (
    <button
      className="grab-list-state error actionable"
      type="button"
      onClick={onNavigate}
    >
      <CircleAlert size={29} />
      <strong>请先激活服务者身份</strong>
      <p>{message}</p>
      <span>点击前往服务者申请</span>
    </button>
  );
}

export default function GrabOrdersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [orderNo, setOrderNo] = useState("");
  const [appliedOrderNo, setAppliedOrderNo] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const providerQuery = useQuery({
    queryKey: ["provider", "me"],
    queryFn: getMyProviderOnboarding,
  });
  const providerStatus = String(providerQuery.data?.status || "").toUpperCase();
  const canGrabOrders = providerStatus === "ACTIVE";
  const openOrdersQuery = useQuery({
    queryKey: ["orders", "open", appliedOrderNo],
    queryFn: () => listOpenQuickOrders({ orderNo: appliedOrderNo }),
    enabled: canGrabOrders,
  });
  const grabMutation = useMutation({
    mutationFn: grabQuickOrder,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["orders", "open"] });
      await queryClient.invalidateQueries({ queryKey: ["orders", "mine", "provider"] });
      setSelectedOrderId(null);
      showToast("抢单成功，请确认服务信息");
      navigate("/orders?view=received");
    },
    onError: (error) => {
      showToast(getApiErrorMessage(error, "抢单失败，请稍后重试"), "error");
    },
  });
  const openOrders = openOrdersQuery.data?.items || [];

  function handleSearch(event) {
    event.preventDefault();
    setAppliedOrderNo(orderNo.trim().toUpperCase());
  }

  return (
    <section className="page-container grab-orders-page">
      <div className="grab-order-intro">
        <span className="grab-order-icon"><HandCoins size={26} /></span>
        <div>
          <h1>抢单</h1>
          <p>浏览可承接的设计需求，查看详情后再确认抢单。</p>
        </div>
      </div>

      <div className="grab-order-notes">
        <article><CircleCheck size={19} /><div><strong>先看详情，再确认抢单</strong><p>需求摘要、预算、交付与验收标准都会在详情中展示。</p></div></article>
        <article><ShieldCheck size={19} /><div><strong>平台并发保护</strong><p>平台会原子锁定订单，已被抢走的需求无法重复提交。</p></div></article>
      </div>

      <form className="grab-order-search" onSubmit={handleSearch}>
        <Search size={18} />
        <input
          value={orderNo}
          onChange={(event) => setOrderNo(event.target.value)}
          placeholder="按订单号筛选（选填）"
          aria-label="按订单号筛选"
        />
        <button type="submit">筛选</button>
      </form>

      {!canGrabOrders ? (
        <ProviderActivationNotice
          provider={providerQuery.data}
          isLoading={providerQuery.isPending}
          isError={providerQuery.isError}
          error={providerQuery.error}
          onNavigate={() => navigate("/settings/provider")}
        />
      ) : openOrdersQuery.isPending ? (
        <div className="grab-list-state">正在加载可抢需求……</div>
      ) : null}
      {canGrabOrders && openOrdersQuery.isError ? (
        <div className="grab-list-state error">
          {getApiErrorMessage(openOrdersQuery.error, "可抢需求加载失败")}
        </div>
      ) : null}
      {canGrabOrders && openOrdersQuery.isSuccess && !openOrders.length ? (
        <div className="grab-list-state">
          <HandCoins size={29} />
          <strong>{appliedOrderNo ? "没有匹配的可抢需求" : "暂时没有可抢需求"}</strong>
          <p>新的设计需求开放后会显示在这里。</p>
        </div>
      ) : null}
      {openOrders.length ? (
        <div className="grab-order-list">
          {openOrders.map((order) => {
            const isReservation = order.dispatch_type === "RESERVATION";
            return (
              <article className="grab-order-card" key={order.id}>
                <div className="grab-order-card-main">
                  <div className="grab-order-card-heading">
                    <span>{order.order_no}</span>
                    {order.is_directed ? <em>定向</em> : null}
                  </div>
                  <h2>{order.title}</h2>
                  <p>{order.requirement_summary || "暂无需求摘要"}</p>
                  <div className="grab-order-card-meta">
                    <span>{isReservation ? <CalendarClock size={14} /> : <Zap size={14} />}{isReservation ? "预约需求" : "即时需求"}</span>
                    <span><Clock3 size={14} />{order.expected_draft_label || order.preferred_start_label || "时间待沟通"}</span>
                    <span>发布于 {formatDate(order.created_at)}</span>
                  </div>
                </div>
                <div className="grab-order-card-side">
                  <strong>¥{formatAmount(order.amount_yuan)}</strong>
                  <button type="button" onClick={() => setSelectedOrderId(order.id)}>查看详情</button>
                </div>
              </article>
            );
          })}
        </div>
      ) : null}

      {selectedOrderId ? (
        <OrderPreview
          orderId={selectedOrderId}
          onClose={() => setSelectedOrderId(null)}
          onGrab={(id) => grabMutation.mutate(id)}
          grabbing={grabMutation.isPending}
        />
      ) : null}
    </section>
  );
}
